import React, { ReactNode, PropsWithChildren } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@wove/web-ui'
import { X } from 'lucide-react'

interface BaseModalProps {
  headerIconComponent?: ReactNode
  headerIcon?: string
  headerIconAlt?: string
  title?: string
  body?: ReactNode
  buttons?: {
    title: string
    type: 'outline' | 'default' | 'secondary'
    onClick: () => void
    disabled?: boolean
    loading?: boolean
  }[]
  fillButtons?: boolean
  type?: 'dialog'
  onCloseButton?: () => void
  width?: string
  contentColor?: string
  alignItems?: string
  isOpen: boolean
}

const BaseModal = ({
  headerIconComponent,
  headerIcon,
  headerIconAlt,
  title,
  body,
  buttons,
  fillButtons,
  onCloseButton,
  width = 'auto',
  children,
  contentColor = '#f4f6f8',
  alignItems,
  isOpen,
}: PropsWithChildren<BaseModalProps>) => {
  const content = (
    <>
      {headerIconComponent && <div className="mb-8">{headerIconComponent}</div>}
      {headerIcon && <img src={headerIcon} alt={headerIconAlt} className="mb-8" />}
      {body && <p className="text-sm font-medium leading-[18px] m-0 mb-8">{body}</p>}
      {children}
      {buttons && (
        <DialogFooter>
          <div
            className={`
              flex gap-[10px] self-stretch text-center w-full
              ${fillButtons ? 'justify-between' : 'justify-end'}
            `}
          >
            {buttons.map((btn, index) => {
              const preset = btn.type
              return (
                <div key={index} className={fillButtons ? 'flex-1' : ''}>
                  <Button
                    variant={preset}
                    disabled={btn.disabled || btn.loading}
                    className="w-full"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault()
                      btn.onClick()
                    }}
                    loading={btn.loading}
                  >
                    {btn.title}
                  </Button>
                </div>
              )
            })}
          </div>
        </DialogFooter>
      )}
    </>
  )

  return (
    <Dialog open={isOpen} onOpenChange={onCloseButton}>
      <DialogContent
        className={`
          p-8 max-w-[95vw] max-h-[90vh] overflow-y-auto
          ${alignItems ? `items-${alignItems}` : ''}
        `}
        style={{
          width,
          background: contentColor,
        }}
      >
        {title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {onCloseButton && (
              <button
                onClick={onCloseButton}
                className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            )}
          </DialogHeader>
        )}
        {content}
      </DialogContent>
    </Dialog>
  )
}

export default BaseModal
